import React from 'react';
import { Helmet } from 'react-helmet';
import { ArrowDown } from 'lucide-react';
import appStoreBadge from '../assets/images/app-store-badge.svg';
import googlePlayBadge from '../assets/images/google-play-badge.svg';
import { useStoreLinks } from '../lib/useStoreLinks';

const AppDownloadPage: React.FC = () => {
  const { appStoreUrl, playStoreUrl, onAppStoreClick, onPlayStoreClick } = useStoreLinks();

  return (
    <div
      className="font-display min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: '#502366' }}
    >
      <Helmet>
        <title>Download DINK Social - App for Childfree Couples</title>
        <meta
          name="description"
          content="Download DINK Social on iOS or Android. The social app built exclusively for childfree couples to connect, discover events, and build meaningful friendships."
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Download DINK Social - App for Childfree Couples" />
        <meta property="og:description" content="Download DINK Social on iOS or Android. The social app built exclusively for childfree couples to connect, discover events, and build meaningful friendships." />
        <meta property="og:url" content="https://dinksocial.com/app" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download DINK Social - App for Childfree Couples" />
        <meta name="twitter:description" content="Download DINK Social on iOS or Android. The social app built exclusively for childfree couples." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/app" />

        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MobileApplication",
                "@id": "https://dinksocial.com/app#ios",
                "name": "DINK Social",
                "operatingSystem": "iOS",
                "applicationCategory": "SocialNetworkingApplication",
                "applicationSubCategory": "Lifestyle",
                "description": "The social app built exclusively for childfree couples to connect, discover events, and build meaningful friendships.",
                "url": "https://apps.apple.com/us/app/dink-social/id6756313023",
                "downloadUrl": "https://apps.apple.com/us/app/dink-social/id6756313023",
                "installUrl": "https://apps.apple.com/us/app/dink-social/id6756313023",
                "inLanguage": "en-US",
                "screenshot": [
                  "https://dinksocial.com/app-screenshot-1.png",
                  "https://dinksocial.com/app-screenshot-2.png"
                ],
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "DINK Social",
                  "url": "https://dinksocial.com",
                  "logo": "https://dinksocial.com/DINKSocial_logo_plum.svg"
                }
              },
              {
                "@type": "MobileApplication",
                "@id": "https://dinksocial.com/app#android",
                "name": "DINK Social",
                "operatingSystem": "Android",
                "applicationCategory": "SocialNetworkingApplication",
                "applicationSubCategory": "Lifestyle",
                "description": "The social app built exclusively for childfree couples to connect, discover events, and build meaningful friendships.",
                "url": "https://play.google.com/store/apps/details?id=com.dinksocial.app",
                "downloadUrl": "https://play.google.com/store/apps/details?id=com.dinksocial.app",
                "installUrl": "https://play.google.com/store/apps/details?id=com.dinksocial.app",
                "inLanguage": "en-US",
                "screenshot": [
                  "https://dinksocial.com/app-screenshot-1.png",
                  "https://dinksocial.com/app-screenshot-2.png"
                ],
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "DINK Social",
                  "url": "https://dinksocial.com",
                  "logo": "https://dinksocial.com/DINKSocial_logo_plum.svg"
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="text-center">
        <img
          src="/DINKSocial_logo_white.svg"
          alt="DINK Social"
          className="h-24 md:h-32 mb-8 block mx-auto translate-x-3"
        />
        <h1
          className="font-playfair italic font-medium text-white/90 text-[20px] md:text-[24px] mb-10"
          style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
        >
          The social app for childfree couples to connect and thrive
        </h1>

        <div className="mb-5 flex flex-col items-center gap-1">
          <p className="text-white text-[15px] md:text-[17px] font-medium tracking-wide">
            Download for free
          </p>
          <ArrowDown className="text-white/80 h-5 w-5 animate-bounce" aria-hidden="true" />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onAppStoreClick}
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={appStoreBadge}
              alt="Download on the App Store"
              className="h-[56px] w-auto"
            />
          </a>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onPlayStoreClick}
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={googlePlayBadge}
              alt="Get it on Google Play"
              className="h-[56px] w-auto"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadPage;
